import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  cloneList, cloneMap, isSameData, isSameList, isSameMap, mapToData,
} from '../../dto/data-model-utils.js';
import {
  DataModel,
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { TicketTemplate } from '../ticket-template';
import { ITicketPoolSeatingModule } from './ticket-pool-seating-module.js';
import { TicketPoolStrategy } from './ticket-pool-strategy.js';
import { ITicketPool } from './ticket-pool.js';

export class ShardGroup implements ICloneable, IDataComparable {
  private constructor(
    public readonly templateId: string,
    public readonly limit: number,
    public readonly shards: string[],
  ) {}

  static create(args: {
    templateId: string,
    limit: number,
    shards: string[],
  }): ShardGroup {
    return new ShardGroup(
        args.templateId,
        args.limit,
        args.shards,
    );
  }

  static fromMap(map: SerializedData): ShardGroup {
    return this.create({
      templateId: MapUtils.getString(map, 'templateId'),
      limit: MapUtils.getNumber(map, 'limit'),
      shards: MapUtils.getList(map, 'shards', (x) => JSONUtils.decodeString(x)),
    });
  }

  toMap(): SerializedData {
    return {
      templateId: this.templateId,
      limit: this.limit,
      shards: this.shards,
    };
  }

  clone(): this {
    return ShardGroup.create({
      templateId: this.templateId,
      limit: this.limit,
      shards: cloneList(this.shards),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.templateId === this.templateId &&
      other.limit === this.limit &&
      isSameList(other.shards, this.shards);
  }
}

export class ShardedTicketPool extends ITicketPool {
  static readonly TYPE_ID: string = '1f40ccc0ecda4da893d8aad86048819c';

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly shardGroups: Map<string, ShardGroup>,
    public readonly reservedTickets: Map<string, number>,
  ) {
    super();
  }

  get $typeId(): string {
    return ShardedTicketPool.TYPE_ID;
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    eventId: string,
    shardGroups: Map<string, ShardGroup>,
    reservedTickets: Map<string, number>,
  }): ShardedTicketPool {
    return new ShardedTicketPool(
        args.businessRef,
        args.eventId,
        args.shardGroups,
        args.reservedTickets,
    );
  }

  public get strategy(): TicketPoolStrategy {
    return TicketPoolStrategy.Sharded;
  }

  public getSeatingModule(): ITicketPoolSeatingModule | null {
    return null;
  }

  public getTicketCount(templateId: string): number {
    return this.reservedTickets.get(templateId) ?? 0;
  }

  public isSoldOut(template: TicketTemplate): boolean {
    if(template.quantity.isInfinite) return false;

    return this.getTicketCount(template.uniqueId) >= template.quantity.value;
  }

  static fromMap(context: SerializationContext, map: SerializedData): ShardedTicketPool {
    return this.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getString(map, 'eventId'),
      shardGroups: MapUtils.getMap(map, 'shardGroups', (k, v) => ({
        key: k,
        value: ShardGroup.fromMap(JSONUtils.decodeRecord(v)),
      })),
      reservedTickets: MapUtils.getMap(map, 'reservedTickets', (k, v) => ({
        key: k,
        value: JSONUtils.decodeNumber(v),
      })),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      businessRef: this.businessRef.encode(),
      eventId: this.eventId,
      shardGroups: mapToData(this.shardGroups, (k, v) => ({
        key: k,
        value: v.toMap(),
      })),
      reservedTickets: mapToData(this.reservedTickets, (k, v) => ({
        key: k,
        value: v,
      })),
    };
  }

  clone(): this {
    return ShardedTicketPool.create({
      businessRef: this.businessRef.clone(),
      eventId: this.eventId,
      shardGroups: cloneMap(this.shardGroups, (v) => v.clone()),
      reservedTickets: cloneMap(this.reservedTickets),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      isSameMap(other.shardGroups, this.shardGroups, (v1, v2) => isSameData(v1, v2)) &&
      isSameMap(other.reservedTickets, this.reservedTickets);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof ShardedTicketPool;
  }

  static assertType(order: DataModel): order is ShardedTicketPool {
    return order instanceof ShardedTicketPool;
  }
}

export class ShardedTicketPoolBuilder extends DataModelBuilder<ShardedTicketPool> {
  build(context: SerializationContext, map: SerializedData): ShardedTicketPool {
    return ShardedTicketPool.fromMap(context, map);
  }
}
